'use client';

import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, Shield, Gavel, Gamepad2, Wrench } from 'lucide-react';
import { commands, CommandCategory } from '@/lib/mock-data';

const categoryConfig = {
  security: {
    label: 'Security',
    icon: Shield,
    color: 'text-red-500',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/50',
  },
  moderation: {
    label: 'Moderation',
    icon: Gavel,
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/50',
  },
  fun: {
    label: 'Fun',
    icon: Gamepad2,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/50',
  },
  utility: {
    label: 'Utility',
    icon: Wrench,
    color: 'text-green-500',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/50',
  },
};

export function CommandDirectory() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<CommandCategory | 'all'>('all');

  const filteredCommands = useMemo(() => {
    return commands.filter((command) => {
      const matchesSearch =
        searchQuery === '' ||
        command.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        command.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        command.syntax.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesCategory =
        selectedCategory === 'all' || command.category === selectedCategory;

      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, selectedCategory]);

  const commandsByCategory = useMemo(() => {
    if (selectedCategory === 'all') {
      return Object.keys(categoryConfig).map((cat) => ({
        category: cat as CommandCategory,
        commands: filteredCommands.filter((cmd) => cmd.category === cat),
      }));
    }
    return [
      {
        category: selectedCategory as CommandCategory,
        commands: filteredCommands,
      },
    ];
  }, [filteredCommands, selectedCategory]);

  return (
    <section id="commands" className="py-24 bg-gradient-to-b from-[#0f0f0f] to-[#1a1a1a]">
      <div className="container mx-auto px-4">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Complete Command Directory
          </h2>
          <p className="text-xl text-[#b9bbbe] max-w-2xl mx-auto">
            Browse all {commands.length} commands available in Yokki
          </p>
        </motion.div>

        {/* Search and filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="max-w-4xl mx-auto mb-12"
        >
          {/* Search bar */}
          <div className="relative mb-6">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#b9bbbe]" />
            <Input
              type="text"
              placeholder="Search commands..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 py-6 bg-[#252525] border-[#b9bbbe]/20 text-white placeholder:text-[#b9bbbe]/50 focus:border-[#5865F2] text-lg"
            />
          </div>

          {/* Category filters */}
          <div className="flex flex-wrap gap-3 justify-center">
            <Badge
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              className={`cursor-pointer px-6 py-2 text-sm font-medium transition-all ${
                selectedCategory === 'all'
                  ? 'bg-[#5865F2] text-white hover:bg-[#4752C4]'
                  : 'bg-transparent border-[#b9bbbe]/20 text-[#b9bbbe] hover:bg-[#252525]'
              }`}
              onClick={() => setSelectedCategory('all')}
            >
              All Commands
            </Badge>
            {(Object.keys(categoryConfig) as CommandCategory[]).map((cat) => {
              const config = categoryConfig[cat];
              const Icon = config.icon;
              return (
                <Badge
                  key={cat}
                  variant={selectedCategory === cat ? 'default' : 'outline'}
                  className={`cursor-pointer px-6 py-2 text-sm font-medium transition-all ${
                    selectedCategory === cat
                      ? `${config.bgColor} ${config.borderColor} border ${config.color}`
                      : 'bg-transparent border-[#b9bbbe]/20 text-[#b9bbbe] hover:bg-[#252525]'
                  }`}
                  onClick={() => setSelectedCategory(cat)}
                >
                  <Icon className="h-4 w-4 mr-1" />
                  {config.label}
                </Badge>
              );
            })}
          </div>
        </motion.div>

        {/* Commands list */}
        <div className="max-w-6xl mx-auto space-y-12">
          {commandsByCategory.map(({ category, commands: categoryCommands }) => {
            if (categoryCommands.length === 0) return null;

            const config = categoryConfig[category];
            const Icon = config.icon;

            return (
              <motion.div
                key={category}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                {/* Category header */}
                {selectedCategory === 'all' && (
                  <div className="flex items-center gap-3 mb-6">
                    <div className={`h-10 w-10 rounded-lg ${config.bgColor} flex items-center justify-center`}>
                      <Icon className={`h-6 w-6 ${config.color}`} />
                    </div>
                    <h3 className="text-2xl font-bold text-white">{config.label}</h3>
                    <span className="text-[#b9bbbe]">({categoryCommands.length})</span>
                  </div>
                )}

                {/* Commands grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {categoryCommands.map((command, index) => (
                    <motion.div
                      key={command.id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.05 }}
                    >
                      <Card className="h-full bg-[#1a1a1a]/60 backdrop-blur-sm border-[#b9bbbe]/10 hover:border-[#5865F2]/30 transition-all hover:scale-105">
                        <CardHeader>
                          <CardTitle className="text-lg text-white mb-1">{command.name}</CardTitle>
                          <p className="text-sm text-[#b9bbbe] mb-2">{config.label}</p>
                          <code className="text-sm text-[#5865F2] bg-[#252525] px-3 py-1 rounded font-mono block">
                            {command.syntax}
                          </code>
                        </CardHeader>
                        <CardContent>
                          <CardDescription className="text-[#b9bbbe]">
                            {command.description}
                          </CardDescription>
                          {command.example && (
                            <div className="mt-3 text-xs text-[#b9bbbe]/70">
                              <span className="font-semibold">Example:</span>{' '}
                              <code className="text-[#5865F2]">{command.example}</code>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            );
          })}

          {filteredCommands.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12"
            >
              <p className="text-[#b9bbbe] text-lg">
                No commands found matching your search.
              </p>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}
